import { saveAs } from 'file-saver'

class DocumentsAPI {
  constructor(tokenGetter, baseURL) {
    const clientOptions = {
      headers: {
        'Cache-Control': 'no-cache',
      },
    }

    if (baseURL) {
      clientOptions.baseURL = baseURL
    }

    this._api = $fetch.create(clientOptions)
    this._tokenGetter = tokenGetter
  }

  async getBearerToken() {
    return await this._tokenGetter()
  }

  async uploadDocument(document, documentType, policyNumber, termID, transactionID) {
    const fd = new FormData()
    fd.append('document', document)
    fd.append('documentType', documentType)

    let url = `/v1/policies/${policyNumber}/terms/${termID}/transactions/${transactionID}/uploaddocument`

    if (
      documentType === 'CCAuthorization' ||
      documentType === 'EFTAuthorization'
    ) {
      url = `/v1/billing/payment_information/policy/${policyNumber}/term/${termID}/transaction/${transactionID}/upload_payment_authorization_form`
    }

    return await this._api(url, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${await this.getBearerToken()}`,
      },
      body: fd,
    })
  }

  async uploadAutoDocument(document, policyNumber, termID, transactionID) {
    const fd = new FormData()
    fd.append('document', document)

    const url = `/v1/policies/${policyNumber}/terms/${termID}/transactions/${transactionID}/uploadautodocument`

    return await this._api(url, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${await this.getBearerToken()}`,
      },
      body: fd,
    })
  }

  async getDocumentBlob(filePath) {
    const fileArrayBuffer = await this._api(`/v2/documents/download/${filePath}`, {
      method: 'GET',
      responseType: 'arrayBuffer',
      headers: {
        Authorization: `Bearer ${await this.getBearerToken()}`,
      },
    })

    return new Blob([fileArrayBuffer])
  }

  async getDocumentFile(filePath, fileName) {
    const documentBlob = await this.getDocumentBlob(filePath)
    return new File([documentBlob], fileName)
  }

  async downloadDocument(filePath, fileName) {
    const file = await this.getDocumentFile(filePath, fileName)
    await saveAs(file)
  }

  async uploadDraftDocument(document, documentType, policyNumber, termID, transactionID) {
    const fd = new FormData()
    fd.append('document', document)
    fd.append('documentType', documentType)

    const url = `/v1/policies/${policyNumber}/terms/${termID}/transactions/${transactionID}/draftDocs`

    return await this._api(url, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${await this.getBearerToken()}`,
      },
      body: fd,
    })
  }

  async searchDraftDocuments(policyNumber, termID, transactionID) {
    const url = `/v1/policies/${policyNumber}/terms/${termID}/transactions/${transactionID}/draftDocs`

    return await this._api(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${await this.getBearerToken()}`,
      },
    })
  }

  async deleteDraftDocument(documentID, policyNumber, termID, transactionID) {
    const url = `/v1/policies/${policyNumber}/terms/${termID}/transactions/${transactionID}/draftDocs/${documentID}`

    return await this._api(url, {
      method: 'DELETE',
      headers: {
        Authorization: `Bearer ${await this.getBearerToken()}`,
      },
    })
  }

  async getDraftDocument(documentID, policyNumber, termID, transactionID) {
    const url = `/v1/policies/${policyNumber}/terms/${termID}/transactions/${transactionID}/draftDocs/${documentID}`

    const fileArrayBuffer = await this._api(url, {
      method: 'GET',
      responseType: 'arrayBuffer',
      headers: {
        Authorization: `Bearer ${await this.getBearerToken()}`,
      },
    })

    const documentBlob = new Blob([fileArrayBuffer])
    return new File([documentBlob], 'cancellation')
  }

  async generateDocument(policyNumber, termID, transactionID, documentType) {
    const url = `/v1/policies/${policyNumber}/terms/${termID}/transactions/${transactionID}/document`

    const file = await this._api(url, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${await this.getBearerToken()}`,
      },
      body: {
        documentType,
      },
      responseType: 'blob',
    })

    return file
  }
}

export default defineNuxtPlugin(() => {
  const { public: config } = useRuntimeConfig()
  const authCookie = useCookie(config.authTokenName)

  return {
    provide: {
      documentsAPI: new DocumentsAPI(() => authCookie.value, config.documentsEndpoint),
    },
  }
})
